import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, CheckCircle, Calendar, Bell, FileText, Users, Shield, Phone, Smartphone, AlertCircle, Car, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export const PropietariosPage = () => {
  const handleDownload = () => {
    toast.success('Preparando descarga de VERSO Propietarios...');
  };

  const features = [
    {
      icon: Calendar,
      title: 'Planes de Mantenimiento',
      description: 'Control completo de mantenimientos requeridos. Información actualizada desde la nube VERSO o ingreso manual.',
      benefits: ['Seguimiento automático', 'Alertas inteligentes', 'Historial completo']
    },
    {
      icon: Bell,
      title: 'Recordatorios Automáticos',
      description: 'Notificaciones oportunas para próximos mantenimientos, nunca más olvides el servicio de tu vehículo.',
      benefits: ['Push notifications', 'Alertas anticipadas', 'Personalizable']
    },
    {
      icon: FileText,
      title: 'Historial Completo',
      description: 'Registro detallado de todos los mantenimientos realizados con acceso desde cualquier lugar.',
      benefits: ['En la nube', 'Acceso 24/7', 'Evidencia fotográfica']
    },
    {
      icon: Users,
      title: 'Gestión de Flotas',
      description: 'Administra múltiples vehículos o flotas completas desde un solo lugar de manera eficiente.',
      benefits: ['Múltiples vehículos', 'Dashboard unificado', 'Reportes por flota']
    },
    {
      icon: Shield,
      title: 'Seguros y Pico y Placa',
      description: 'Seguimiento del vencimiento de seguros y recordatorios automáticos de días de pico y placa en tu ciudad.',
      benefits: ['Alertas de vencimiento', 'Calendario pico y placa', 'Documentos digitales']
    },
    {
      icon: Phone,
      title: 'Asistencia Técnica',
      description: 'Acceso directo a técnicos mecánicos para recibir consejos en caso de avería o emergencia.',
      benefits: ['Soporte en tiempo real', 'Diagnóstico remoto', 'Ahorra en grúas']
    }
  ];

  const insuranceFeatures = [
    {
      title: 'Integración con Aseguradoras',
      description: 'Las aseguradoras pueden vincular VERSO Propietarios para que sus clientes gestionen vehículos asegurados.',
      icon: Shield
    },
    {
      title: 'Optimización de Grúas',
      description: 'Evita solicitudes innecesarias de grúa con asesoría técnica remota, ahorrando costos significativos.',
      icon: TrendingUp
    },
    {
      title: 'Asistencia Preventiva',
      description: 'Recibe consejos de expertos antes de solicitar servicios de emergencia, optimizando recursos.',
      icon: AlertCircle
    }
  ];

  const benefits = [
    'Nunca olvides un mantenimiento importante',
    'Mantén el valor de tu vehículo con historial completo',
    'Gestiona múltiples vehículos desde una app',
    'Recibe asesoría técnica en emergencias',
    'Accede a tu historial desde cualquier dispositivo',
    'Sincronización automática con talleres VERSO',
    'Alertas de vencimientos de seguros',
    'Recordatorios de pico y placa personalizados'
  ];

  const stats = [
    { value: '400k+', label: 'Descargas' },
    { value: '100%', label: 'Gratis' },
    { value: '24/7', label: 'Asistencia' },
    { value: 'iOS/Android', label: 'Disponible' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/30 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
                <Car className="text-blue-400" size={16} />
                <span className="text-blue-300 text-sm font-medium">VERSO PROPIETARIOS</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Gestiona tu vehículo
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  de manera inteligente
                </span>
              </h1>
              <p className="text-gray-400 text-xl mb-8 leading-relaxed">
                App móvil completa para propietarios de vehículos. Controla mantenimientos, recibe recordatorios, 
                gestiona flotas y accede a asistencia técnica desde tu smartphone.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleDownload}
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
                >
                  <Download className="mr-2" size={20} />
                  Descargar Gratis
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10 px-8 py-6 text-lg rounded-xl"
                >
                  <Smartphone className="mr-2" size={20} />
                  Ver en Acción
                </Button>
              </div>

              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>✓ 100% Gratis</span>
                <span>•</span>
                <span>✓ iOS y Android</span>
                <span>•</span>
                <span>✓ Sin anuncios</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-blue-500/20 p-6 text-center hover:border-blue-500/40 transition-all">
                  <div className="text-4xl font-bold text-blue-400 mb-2">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Funcionalidades Principales</h2>
            <p className="text-gray-400 text-lg">Todo lo que necesitas para cuidar tu vehículo</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-blue-500/20 p-6 hover:border-blue-500/40 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-blue-400" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{feature.description}</p>
                  <div className="space-y-1">
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                        <span className="text-gray-500 text-xs">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insurance Integration */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-black to-slate-900"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Integración con Aseguradoras</h2>
            <p className="text-gray-400 text-lg">Optimiza costos y mejora el servicio a clientes asegurados</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {insuranceFeatures.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-blue-500/20 p-8 hover:border-blue-500/40 transition-all duration-300 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-blue-400" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30 backdrop-blur-sm p-8">
              <div className="text-center mb-6">
                <Smartphone className="text-blue-400 mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-white mb-2">App Móvil Completa</h3>
                <p className="text-gray-300">Disponible para iOS y Android</p>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 border border-blue-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-400 mb-1">100%</div>
                  <div className="text-gray-400 text-sm">Gratis para Siempre</div>
                </div>
                <div className="bg-white/5 border border-blue-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-400 mb-1">400k+</div>
                  <div className="text-gray-400 text-sm">Usuarios Activos</div>
                </div>
                <div className="bg-white/5 border border-blue-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-400 mb-1">4.8★</div>
                  <div className="text-gray-400 text-sm">Calificación Promedio</div>
                </div>
              </div>
            </Card>

            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                ¿Por qué elegir
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  VERSO Propietarios?
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                La app más completa para gestionar tu vehículo con tecnología de punta
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-blue-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30 backdrop-blur-sm p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Descarga VERSO Propietarios Hoy
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Únete a más de 400,000 usuarios que ya confían en VERSO para cuidar sus vehículos
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleDownload}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-12 py-6 text-lg rounded-xl"
              >
                <Download className="mr-2" size={20} />
                Descargar para iOS
              </Button>
              <Button 
                onClick={handleDownload}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-12 py-6 text-lg rounded-xl"
              >
                <Download className="mr-2" size={20} />
                Descargar para Android
              </Button>
            </div>
            <p className="text-gray-400 text-sm mt-6">
              100% Gratis • Sin Anuncios • Actualizaciones Constantes
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};
